/* ELMORE 2026 PERFORMANCE + INTERACTIONS — DT progressive reveal v2 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const compactMobile = window.matchMedia('(max-width: 749px)').matches;
  const supportsObserver = 'IntersectionObserver' in window;
  let revealObserver;

  const getObserver = () => {
    if (reduced || !supportsObserver) return null;
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        entry.target.removeAttribute('data-elmore-await');
        revealObserver.unobserve(entry.target);
      }
    }, { rootMargin: '0px 0px -3% 0px', threshold: 0.025 });
    return revealObserver;
  };

  const prepare = (node, observer) => {
    if (node.dataset.elmoreReady === 'true') return;
    node.dataset.elmoreReady = 'true';
    if (!observer || node.getBoundingClientRect().top < window.innerHeight + 30) {
      node.classList.add('is-visible');
      node.removeAttribute('data-elmore-await');
      return;
    }
    node.setAttribute('data-elmore-await', '');
    observer.observe(node);
  };

  const initReveal = (root = document) => {
    /* No delayed reveal work or geometry reads on performance-sensitive mobile. */
    if (compactMobile) return;
    const observer = getObserver();
    // Shopify section wrappers, plus existing hand-picked editorial sections.
    const candidates = new Set(root.querySelectorAll('[data-elmore-reveal], #MainContent > .shopify-section, #MainContent[data-template^="collection"] .product-grid__item'));
    if (root.matches?.('[data-elmore-reveal], #MainContent > .shopify-section, #MainContent[data-template^="collection"] .product-grid__item')) candidates.add(root);
    candidates.forEach((node) => prepare(node, observer));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initReveal(), { once: true });
  } else {
    initReveal();
  }
  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
  // BFCache restores should never leave an offscreen section permanently hidden.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('[data-elmore-await]').forEach(node => {
      node.classList.add('is-visible');
      node.removeAttribute('data-elmore-await');
      revealObserver?.unobserve(node);
    });
  });
})();

/* ELMORE GRID COMMERCE */
(() => {
  const money = (cents) => {
    const value = Math.max(0, Number(cents || 0));
    const rupees = value / 100;
    return 'Rs.' + rupees.toLocaleString('en-PK', {
      minimumFractionDigits: value % 100 ? 2 : 0,
      maximumFractionDigits: 2
    });
  };

  const qtyFor = (commerce) => Math.max(1, Number(commerce?.querySelector('[data-en-grid-qty-value]')?.textContent || 1));

  const updateOffer = (commerce, qty) => {
    const type = commerce?.dataset.offerType || '';
    const message = commerce?.querySelector('[data-en-card-offer-message]');
    const fill = commerce?.querySelector('[data-en-card-offer-fill]');
    if (!type || !message || !fill) return;
    const buy = type === 'b2g1' ? 2 : 1;
    const freebies = Math.floor(qty / buy);
    const remainder = qty % buy;
    const need = remainder === 0 ? buy : buy - remainder;
    const pct = type === 'b1g1' ? 100 : (remainder === 0 ? 100 : Math.round((remainder / buy) * 100));
    if (freebies > 0) {
      const nextAt = (freebies + 1) * buy;
      message.textContent = freebies === 1
        ? '1 FREE unlocked' + (type === 'b2g1' ? ' · add ' + Math.max(1, nextAt - qty) + ' more for another' : '')
        : freebies + ' FREE unlocked';
    } else {
      message.textContent = 'Add ' + need + ' more → 1 FREE';
    }
    fill.style.transform = 'scaleX(' + Math.max(0, Math.min(1, pct / 100)) + ')';
  };

  const updateCommerce = (commerce) => {
    if (!commerce) return;
    const qty = qtyFor(commerce);
    const unit = Number(commerce.dataset.unitPrice || 0);
    const compare = Number(commerce.dataset.comparePrice || 0);
    const total = commerce.querySelector('[data-en-grid-total]');
    const compareNode = commerce.querySelector('[data-en-grid-compare]');
    if (total) total.textContent = money(unit * qty);
    if (compareNode) {
      compareNode.hidden = !(compare > unit);
      if (compare > unit) compareNode.textContent = money(compare * qty);
    }
    commerce.querySelectorAll('input[name="quantity"]').forEach((input) => { input.value = String(qty); });
    updateOffer(commerce, qty);
  };

  const syncModalQuantity = (qty) => {
    let tries = 0;
    const find = () => {
      tries += 1;
      const dialog = document.querySelector('quick-add-modal dialog[open], quick-add-modal[open] dialog, .quick-add-modal dialog[open]');
      if (dialog) {
        dialog.querySelectorAll('input[name="quantity"], quantity-selector input, .quantity-selector input').forEach((input) => {
          input.value = String(qty);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        return;
      }
      if (tries < 12) setTimeout(find, 90);
    };
    setTimeout(find, 30);
  };

  const init = (root = document) => root.querySelectorAll('[data-en-grid-commerce]').forEach(updateCommerce);

  document.addEventListener('click', (event) => {
    const qtyButton = event.target.closest('[data-en-grid-qty]');
    if (qtyButton) {
      event.preventDefault();
      event.stopPropagation();
      const commerce = qtyButton.closest('[data-en-grid-commerce]');
      if (!commerce) return;
      const valueNode = commerce.querySelector('[data-en-grid-qty-value]');
      const configuredMax = Number(commerce.dataset.max || 99);
      const handle = commerce.dataset.productHandle || '';
      const mappedMax = Number(window.elmoreQtyConfig?.rulesByHandle?.[handle] || 0);
      const max = mappedMax > 0 ? mappedMax : configuredMax;
      let value = qtyFor(commerce);
      if (qtyButton.dataset.enGridQty === 'plus') {
        if (value >= max) { window.elmoreShowQtyLimit?.(max); return; }
        value += 1;
      } else value = Math.max(1, value - 1);
      if (valueNode) valueNode.textContent = String(value);
      updateCommerce(commerce);
      return;
    }

    const action = event.target.closest('[data-en-grid-commerce] .quick-add__button');
    if (!action) return;
    const commerce = action.closest('[data-en-grid-commerce]');
    if (!commerce) return;
    const qty = qtyFor(commerce);
    commerce.querySelectorAll('input[name="quantity"]').forEach((input) => { input.value = String(qty); });
    if (action.classList.contains('quick-add__button--choose')) syncModalQuantity(qty);
  }, true);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* ELMORE CART EVENT BRIDGE */
document.addEventListener('shopify:cart:lines-update', function(event) {
  const detail = { source: 'elmore-horizon', action: event.action || '' };
  document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
  window.dispatchEvent(new CustomEvent('cart:updated', { detail }));
});

/* ELMORE RESPONSIVE CART + PDP BRIDGE */
(() => {
  const drawer = () => document.querySelector('theme-drawer#cart-drawer');
  let pendingTimer = 0;
  let cleanupTimer = 0;

  const markOpen = (open) => {
    document.documentElement.classList.toggle('en26-cart-open', open);
    document.body?.classList.toggle('en26-cart-open', open);
  };

  const ensureCartLoader = () => {
    const panel = document.querySelector('#cart-drawer .theme-drawer__dialog');
    if (!panel || panel.querySelector('[data-en26-cart-loader]')) return;
    const loader = document.createElement('div');
    loader.className = 'en26-cart-loader';
    loader.setAttribute('data-en26-cart-loader', '');
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = '<span class="en26-cart-loader__spinner" aria-hidden="true"></span><span><strong>Adding to your bag…</strong><small>Updating your cart.</small></span>';
    panel.appendChild(loader);
  };

  const setCartPending = (pending) => {
    clearTimeout(pendingTimer);
    ensureCartLoader();
    document.documentElement.classList.toggle('en26-cart-pending', pending);
    if (pending) pendingTimer = setTimeout(() => document.documentElement.classList.remove('en26-cart-pending'), 8000);
  };

  const cleanLegacyRibbon = () => {
    const root = document.querySelector('#cart-drawer');
    if (!root) return;
    const nodes = root.querySelectorAll('.cart-drawer__content div,.cart-drawer__content span,.cart-drawer__content p');
    for (const el of nodes) {
      if (el.closest('.en26-cart-offer-tape')) continue;
      const txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (/^BUY\s*[12]\s*GET\s*1\s*FREE\b/i.test(txt) && txt.length > 40) el.style.setProperty('display', 'none', 'important');
    }
  };

  const scheduleCleanup = (delay = 90) => {
    clearTimeout(cleanupTimer);
    cleanupTimer = setTimeout(cleanLegacyRibbon, delay);
  };

  document.addEventListener('theme-drawer:open', (e) => {
    if (e.target?.id !== 'cart-drawer') return;
    markOpen(true);
    scheduleCleanup(60);
  });
  document.addEventListener('theme-drawer:close', (e) => {
    if (e.target?.id !== 'cart-drawer') return;
    markOpen(false);
    setCartPending(false);
  });

  document.addEventListener('click', (e) => {
    const shortcut = e.target.closest('[data-en26-gallery-smart]');
    if (!shortcut) return;
    const action = shortcut.dataset.en26GallerySmart;
    const root = shortcut.closest('.product-information')?.querySelector('[data-en26-smart-actions]') || document.querySelector('[data-en26-smart-actions]');
    const target = root?.querySelector(`[data-en26-open="${action}"]`);
    if (!target) return;
    e.preventDefault();
    e.stopPropagation();
    target.click();
  }, true);

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('button[name="add"],.add-to-cart-button,.quick-add__button:not(.quick-add__button--choose)');
    if (!btn || btn.disabled) return;
    setCartPending(true);
    drawer()?.open?.();
    markOpen(true);
  }, true);

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const started = performance.now();
    const finish = () => {
      const wait = Math.max(0, 220 - (performance.now() - started));
      setTimeout(() => setCartPending(false), wait);
      scheduleCleanup(70);
      setTimeout(cleanLegacyRibbon, 420);
    };
    const update = event.promise;
    if (update && typeof update.finally === 'function') update.finally(finish);
    else finish();
  });
  document.addEventListener('shopify:cart:error', () => setCartPending(false));
})();

/* ELMORE LOW-COST VISUAL OVERRIDES */
(() => {
  if (document.getElementById('en26-performance-overrides')) return;
  const style = document.createElement('style');
  style.id = 'en26-performance-overrides';
  style.textContent = `
#cart-drawer .cart-drawer__summary{background:#fff!important;backdrop-filter:none!important;-webkit-backdrop-filter:none!important}
#cart-drawer .theme-drawer__dialog{position:fixed!important;z-index:92!important}
#cart-drawer .theme-drawer__dialog::backdrop{background:rgba(37,27,34,.18)!important;backdrop-filter:none!important;-webkit-backdrop-filter:none!important}
#cart-drawer .theme-drawer__header{background:#fff!important;backdrop-filter:none!important;-webkit-backdrop-filter:none!important}
.en26-cart-loader{position:absolute;inset:0;z-index:30;display:flex;align-items:center;justify-content:center;gap:12px;padding:24px;background:rgba(255,255,255,.97);opacity:0;visibility:hidden;pointer-events:none;transition:opacity .12s ease,visibility .12s ease}
html.en26-cart-pending #cart-drawer .en26-cart-loader{opacity:1;visibility:visible;pointer-events:auto}
.en26-cart-loader__spinner{width:26px;height:26px;flex:0 0 26px;border:3px solid #f2d8e6;border-top-color:#b45784;border-radius:50%;animation:en26-cart-spin .65s linear infinite}
.en26-cart-loader>span:last-child{display:grid;gap:3px}.en26-cart-loader strong{font-size:.82rem;color:#382b33}.en26-cart-loader small{font-size:.6rem;color:#83757e}
@keyframes en26-cart-spin{to{transform:rotate(360deg)}}
.en26-product-badge--sale{animation:none!important}
#header-component shopify-account{--shopify-account-dialog-position-top:calc(var(--header-group-height,var(--header-height,60px)) + 8px);--shopify-account-radius-base:22px;--shopify-account-radius-button:999px;--shopify-account-radius-button-small:999px;--shopify-account-radius-input:14px}
@media(max-width:989px){
 #header-group .header__row{backdrop-filter:none!important;-webkit-backdrop-filter:none!important;background:#fff!important}
 .add-to-cart-button,.quick-add__button{backdrop-filter:none!important;-webkit-backdrop-filter:none!important}
 #cart-drawer .theme-drawer__dialog{box-shadow:-12px 0 38px rgba(48,32,42,.14)!important}
}
@media(max-width:749px){
 .header-drawer .menu-drawer{will-change:transform;transition:transform .2s cubic-bezier(.22,1,.36,1),opacity .14s ease,visibility .2s step-end!important}
 .header-drawer details.menu-open>.menu-drawer{transition:transform .2s cubic-bezier(.22,1,.36,1),opacity .14s ease,visibility 0s!important}
 .header-drawer .menu-drawer__backdrop{backdrop-filter:none!important;-webkit-backdrop-filter:none!important;transition:opacity .14s ease,visibility .14s ease!important}
 .en26-mobile-account shopify-account{display:grid!important;place-items:center!important;width:36px!important;height:36px!important;min-width:36px!important;--shopify-account-dialog-position-top:calc(env(safe-area-inset-top,0px) + 10px);--shopify-account-radius-base:22px;--shopify-account-radius-button:999px;--shopify-account-radius-button-small:999px;--shopify-account-radius-input:14px}
 .en26-mobile-account__avatar{display:grid!important;place-items:center!important;width:36px!important;height:36px!important;color:#292229!important}
 .en26-mobile-account__avatar svg{display:block!important;width:18px!important;height:18px!important;fill:none!important;stroke:currentColor!important;stroke-width:1.65!important;stroke-linecap:round!important;stroke-linejoin:round!important}
}
@media(prefers-reduced-motion:reduce){.en26-cart-loader__spinner{animation:none}.en26-cart-loader{transition:none}}
`;
  document.head.appendChild(style);
})();

